import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import { sendGtEvent } from 'analytics/analytics';
import { sendBrazeEvent } from 'analytics/braze';

import { getFieldsForSend } from 'components/form-multi/utils';

import { toggleSpinner } from 'actions/appAction';
import { initCalculator } from 'actions/calculatorAction';
import { setExtra } from 'actions/extraAction';
import { changeFormFieldValue, setNewForm, updateFormProperties } from 'actions/formAction';
import { updatePageMessages } from 'actions/messageAction';

import { BR_SEND_MONEY_FROM, BR_SEND_MONEY_FROM_AMOUNT, BR_SEND_MONEY_TO, BR_SEND_MONEY_TO_AMOUNT } from 'constants/braze/brazeEvents';
import { BR_AMOUNT, BR_CHOSEN_COUNTRY, BR_COUNTRY, BR_CURRENCY, BR_LANGUAGE } from 'constants/braze/brazeParams';
import { REQ_ERROR, VALID_ERROR } from 'constants/Constants';
import { ACCOUNT_TO, DELIVERY_METHOD } from 'constants/fieldsName';
import { GT_SEND_MONEY_FROM_AMOUNT } from 'constants/GoogleAnalyticsEvents';

import { apiFetchData, changeBrowserUrlWithOutReload } from 'helpers/api';
import { countryPropType } from 'helpers/customPropTypes';
import { getLangCountryPrefix, resolveArguments } from 'helpers/utils';

import amplitude from 'amplitude-js';
import { useHandleCloseDropdownMenu } from 'hooks/useHandleCloseDropdownMenu';

import AmountCurrency from './AmountCurrency';
import { AmountRow } from './styles';
import { PromoHintContainer } from '../../form-multi/styles';
import { PromoDangerouslyHtml } from '../../no-auth/common/PromoDangerouslyHtml/PromoDangerouslyHtml';
import PromoInputAmount from '../../no-auth/form/PromoInputAmount';
import PromoCountryList from '../CalculatorDropdown/PromoCountryList';

const sendEvent = ({ lang, ...props }, { commission: commissionData, ...data }) => {
    const {
        restrictionFrom: { ccyIso: ccyIsoFrom },
        restrictionTo: { ccyIso: ccyIsoTo },
    } = commissionData;

    const { countryFrom: previousCountryFrom, countryTo: previousCountryTo } = props.calculator.countryMenu;
    const { countryFrom: chosenFrom, countryTo: chosenTo } = data.countryMenu;

    amplitude.getInstance().logEvent('Calculator Adjusted');

    if (previousCountryFrom.code.toLowerCase().trim() !== chosenFrom.code.toLowerCase().trim()) {
        sendBrazeEvent(BR_SEND_MONEY_FROM, {
            [BR_COUNTRY]: previousCountryFrom ? previousCountryFrom.code : '',
            [BR_CHOSEN_COUNTRY]: chosenFrom.code,
            [BR_CURRENCY]: ccyIsoFrom,
            [BR_LANGUAGE]: lang,
        });
    }

    if (previousCountryTo.code.toLowerCase().trim() !== chosenTo.code.toLowerCase().trim()) {
        sendBrazeEvent(BR_SEND_MONEY_TO, {
            [BR_COUNTRY]: previousCountryTo ? previousCountryTo.code : '',
            [BR_CHOSEN_COUNTRY]: chosenTo.code,
            [BR_CURRENCY]: ccyIsoTo,
            [BR_LANGUAGE]: lang,
        });
    }
};

const sendEventOnBlur = (value, isLanding, isFrom, calculator, lang) => {
    if (calculator?.commission && isLanding) {
        const {
            restrictionFrom: { ccyIso: ccyIsoFrom },
            restrictionTo: { ccyIso: ccyIsoTo },
        } = calculator.commission;

        const { countryFrom: chosenFrom, countryTo: chosenTo } = calculator.countryMenu;

        amplitude.getInstance().logEvent('Calculator Adjusted');

        if (isFrom) {
            sendBrazeEvent(BR_SEND_MONEY_FROM_AMOUNT, {
                [BR_CURRENCY]: ccyIsoFrom,
                [BR_CHOSEN_COUNTRY]: chosenFrom.code,
                [BR_AMOUNT]: value,
                [BR_LANGUAGE]: lang,
            });
        } else {
            sendBrazeEvent(BR_SEND_MONEY_TO_AMOUNT, {
                [BR_CURRENCY]: ccyIsoTo,
                [BR_CHOSEN_COUNTRY]: chosenTo.code,
                [BR_AMOUNT]: value,
                [BR_LANGUAGE]: lang,
            });
        }
    }
};

const getUrl = (country, currency, isLanding, isFrom, props) => {
    const currencyId = isFrom ? props.countryTo.currencies[0].id : props.countryFrom.currencies[0].id;
    const fromCountry = isFrom ? country : props.countryFrom;
    const fromCode = isLanding ? fromCountry.seoNameFrom : fromCountry.code;
    const toCountry = isFrom ? props.countryTo : country;
    const toCode = isLanding ? toCountry.seoNameTo : toCountry.code;
    const dependedURI = isLanding ? resolveArguments(props.localeDependentURI, [fromCode, toCode]) : `/send/${fromCode}/${toCode}`;
    const fromCurrId = isFrom ? currency.id : currencyId;
    const toCurrId = isFrom ? currencyId : currency.id;

    return `${
        isLanding ? getLangCountryPrefix(props.lang, props.country) : '/auth'
    }${dependedURI}?fromCurrId=${fromCurrId}&toCurrId=${toCurrId}&isFrom=${!isFrom}`;
};

const getErrorText = (field, props) => {
    const errorMessageKey = field.value ? VALID_ERROR : REQ_ERROR;
    return field.errorMessage ? field.errorMessage : props.jsMessages[errorMessageKey];
};

const Amount = ({
    isLock = false,
    amountFieldBean = {},
    isLanding = false,
    enabledValidateMinMaxAmount = true,
    countryBean,
    countryList,
    isFrom,
    ...props
}) => {
    const [showCountryList, setShowCountryList] = useState(true);
    const [selectedCountry, setSelectedCountry] = useState({});

    const wrapperRef = useRef(null);
    const [isOpenDropdownMenu, setOpenDropdownMenu] = useHandleCloseDropdownMenu(wrapperRef);

    const formName = props.formName;

    useEffect(() => {
        setShowCountryList(true);
    }, [isOpenDropdownMenu]);

    const initSelectedCountry = (e, country, currency, setShowCountryList, isLanding, isFrom, props) => {
        props.toggleSpinner(true);
        setShowCountryList(true);

        setOpenDropdownMenu(false);

        const url = getUrl(country, currency, isLanding, isFrom, props);
        if (!isLanding && props.submitRef && props.submitRef.current) {
            props.updateFormProperties(props.formName, url, false).then(() => {
                props.changeFormFieldValue(props.formName, null, ACCOUNT_TO);
                props.changeFormFieldValue(props.formName, null, DELIVERY_METHOD).then(() => {
                    props.submitRef.current.click();
                });
            });
            changeBrowserUrlWithOutReload(url);
        } else {
            apiFetchData(url)
                .then((data) => {
                    props.setNewForm(props.formName, getFieldsForSend(data));
                    props.updatePageMessages(data.messages);
                    props.initCalculator(data);
                    props.setExtra(data.extra);
                    changeBrowserUrlWithOutReload(url, data);
                    props.toggleSpinner(false);

                    sendEvent(props, data);
                })
                .catch(() => props.toggleSpinner(false));
        }
    };

    const selectCountry = (e, country, currency, isCurrencySelected) => {
        e.preventDefault();
        if (country.currencies.length > 1) {
            if (isCurrencySelected) {
                initSelectedCountry(e, country, currency, setShowCountryList, isLanding, isFrom, props);
            } else {
                setShowCountryList(false);
                setSelectedCountry(country);
            }
        } else {
            initSelectedCountry(e, country, currency, setShowCountryList, isLanding, isFrom, props);
        }
    };

    const closeCurrencyButton = (e, country) => {
        e.preventDefault();
        setShowCountryList(true);
        setSelectedCountry(country);
    };

    const fields = props.forms[formName].fields;
    const amountField = fields[amountFieldBean.name];
    const isShowList = countryList && countryList.length > 1;

    return (
        <AmountRow isLock={isLock} ref={wrapperRef}>
            {isOpenDropdownMenu && isShowList ? (
                <PromoCountryList
                    amountField={amountField}
                    showCountryList={showCountryList}
                    selectedCountry={selectedCountry}
                    countryList={countryList}
                    isFrom={isFrom}
                    handlerSelectCountry={selectCountry}
                    handlerCloseButtonCurrencyCountry={closeCurrencyButton}
                    showCurrencies
                />
            ) : (
                <>
                    <PromoInputAmount
                        onBlurHandler={(value) => {
                            sendEventOnBlur(value, isLanding, isFrom, props.calculator, props.lang);
                            sendGtEvent(GT_SEND_MONEY_FROM_AMOUNT);
                            props.onBlurHandler && props.onBlurHandler();
                        }}
                        onFocusHandler={props.onFocusHandler}
                        field={amountField}
                        formName={formName}
                        minAmount={amountFieldBean.minValidValue}
                        maxAmount={amountFieldBean.maxValidValue}
                        multiple={amountFieldBean.multiple}
                        enabledValidateMinMaxAmount={enabledValidateMinMaxAmount}
                        onChangeHandler={props.onChangeHandler}
                        isLanding={isLanding}
                        isFrom={isFrom}
                    >
                        <AmountCurrency
                            isShowList={isShowList}
                            isLanding={isLanding}
                            isFrom={isFrom}
                            amountFieldBean={amountFieldBean}
                            countryBean={countryBean}
                            handlerToggleMenu={() => setOpenDropdownMenu(true)}
                        />
                    </PromoInputAmount>
                    {amountField.isError && (
                        <PromoHintContainer>
                            <PromoDangerouslyHtml dangerouslyHtml={getErrorText(amountField, props)} tag="div" color="error" />
                        </PromoHintContainer>
                    )}
                </>
            )}
        </AmountRow>
    );
};

Amount.propTypes = {
    isFrom: PropTypes.bool.isRequired,
    isSend: PropTypes.bool.isRequired,
    formName: PropTypes.string.isRequired,
    amountFieldBean: PropTypes.object.isRequired,
    countryBean: countryPropType,
    countryTo: countryPropType,
    countryFrom: countryPropType,
    countryList: PropTypes.arrayOf(countryPropType),
    isLock: PropTypes.bool,
};

const mapStateToProps = ({ app, form, language, messages, calculator }) => ({
    forms: form,
    lang: language.lang,
    country: app.country,
    messages: messages.pageMessages,
    jsMessages: messages.jsMessages,
    calculator,
});

export default connect(mapStateToProps, {
    toggleSpinner,
    setNewForm,
    initCalculator,
    updatePageMessages,
    changeFormFieldValue,
    updateFormProperties,
    setExtra,
})(Amount);
